import React from "react";
import { Box, Typography, Paper, Stack } from "@mui/material";
import { motion } from "framer-motion";

const SoftSkillCard = ({ skill, level, color = "#6366f1", delay = 0 }) => {
  const indicators = Array.from({ length: 5 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-20px" }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.1, 0.25, 1] }}
      style={{ height: "100%" }}
    >
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          position: "relative",
          overflow: "hidden",
          cursor: "wait",
          p: 2.5,
          borderRadius: 2,
          background:
            "linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.05)",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-6px)",
            borderColor: `${color}50`,
            background: `linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, ${color}15 100%)`,
            boxShadow: `0 12px 30px -10px ${color}30`,
            "& .watermark": {
              transform: "scale(1.1) rotate(-5deg) translateY(-10px)",
              color: `${color}25`,
            },
          },
        }}
      >
        {/* Background Watermark */}
        <Typography
          className="watermark"
          sx={{
            position: "absolute",
            bottom: -20,
            right: -10,
            fontSize: "6rem",
            fontWeight: 900,
            lineHeight: 1,
            color: `${color}0A`,
            transition: "all 0.5s ease",
            pointerEvents: "none",
            fontStyle: "italic",
            zIndex: 0,
          }}
        >
          0{level}
        </Typography>

        {/* Top Accent Line */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "15%",
            width: "40%",
            height: 2,
            background: `linear-gradient(90deg, transparent, ${color}80, transparent)`,
            boxShadow: `0 0 10px ${color}`,
            opacity: 0.6,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                letterSpacing: "0.5px",
                mb: 0.5,
              }}
            >
              {skill}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.65rem",
                fontWeight: 400,
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Proficiency Index
            </Typography>
          </Box>

          {/* Level Indicators */}
          <Stack
            direction="row"
            spacing={0.8}
            sx={{ mt: 4, alignItems: "center" }}
          >
            {indicators.map((_, index) => {
              const isActive = index < level;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.3,
                    delay: delay + 0.2 + index * 0.08,
                  }}
                >
                  <Box
                    sx={{
                      width: isActive ? 18 : 6,
                      height: 4,
                      borderRadius: 2,
                      bgcolor: isActive ? color : "rgba(255,255,255,0.1)",
                      boxShadow: isActive ? `0 0 10px ${color}80` : "none",
                      transition: "all 0.3s ease",
                    }}
                  />
                </motion.div>
              );
            })}
          </Stack>
        </Box>
      </Paper>
    </motion.div>
  );
};

export default SoftSkillCard;
